import {
  Component,
  EventEmitter,
  HostListener,
  OnInit,
  OnDestroy,
  Output,
  ChangeDetectionStrategy
} from "@angular/core";
import { MessageStatus, MessageType } from "../../../enums/traffic-info-enums";
import { MessageFilter } from "../../../models/message-filter.model";
import { MessageService } from "../../../services/message.service";
import { UtilityService } from "../../../services/utility.service";
import { MessageOverview } from "../../../models/message-overview.model";
import { DialogService } from "../../../services/dialog.service";

@Component({
  selector: "app-message-list",
  standalone: false,
  templateUrl: "./message-list.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./message-list.component.scss"]
})
export class MessageListComponent implements OnInit, OnDestroy {
  @HostListener("window:resize", ["$event"])
  onResize(event: any) {
    this.updateTruncateLength(event.target.innerWidth);
  }
  messageList: MessageOverview[] = [];
  truncateLength: number = 60;
  total: number = 0;
  hasMore: boolean = false;
  isLoadingMore: boolean = false;

  private skip = 0;
  private readonly take = 50;
  private sortBy = 'from';
  private sortDescending = true;

  activeMessageId: any | null = null;
  
  filter = new MessageFilter(
    [MessageStatus.active],
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    [MessageType.TI, MessageType.TOT]
  );

  @Output() clickMessage: EventEmitter<string> = new EventEmitter(); //emit Message ID

  errorText: string |null = null;

  constructor(
    private messageService: MessageService,
    private dialogService: DialogService
  ) {
    this.updateTruncateLength(window.innerWidth);
   }

  ngOnInit(): void {
    this.getMessagesWithFilter();
    
    // Start polling for message list updates
    if (!this.messageService.pollingActive) {
      this.messageService.pollMessageList();
    }
    
    this.messageService.updateList.subscribe(response => {
      this.activeMessageId = response;
      this.getMessagesWithFilter();
    });
    this.messageService.messageChange.subscribe(response => {
      if (this.messageService.messageLength !== this.total) {
        this.skip = 0;
        this.getMessagesWithFilter();
      }
    });

    this.messageService.clickNewMessage.subscribe(response => {
      this.onClickNewMessage();
    });
    this.messageService.clickOpenInNewWindow.subscribe(id => {
      this.activeMessageId = null;
      this.messageService.currentMessage = null;
      this.messageService.originalMessage = null;
    });

    this.messageService.useAsTemplate.subscribe(response => {
      this.activeMessageId = null;
      this.messageService.currentMessage = null;
      this.messageService.originalMessage = null;
    });
  }

  ngAfterViewInit() {
  }

  ngOnDestroy() {
    this.messageService.stopPollMessageList();
  }

  updateTruncateLength(width: number) {
    this.truncateLength = width > 1920 ? 60 : 40;
  }

  statusToClass(status: MessageStatus) {
    switch (status) {
      case MessageStatus.closed:
        return "red";
      case MessageStatus.active:
        return "green";
      case MessageStatus.planned:
        return "blue";
      default:
        return "";
    }
  }

  getMessageTarget(target: MessageType) {
    let response = { text: "", className: "" };
    switch (target.toString()) {
      case "ti":
        response.text = "Appen";
        response.className = "message-app"; 
        break;
      case "tot":
        response.text = "Tåg i Tid";
        response.className = "message-web"; 
        break;
      default:
        response.text = "ERR";
        response.className = "message-err";
    }
    return response;
  }

  getMessageStatus(status: MessageStatus) {
    switch (status) {
      case MessageStatus.closed:
        return "Släckt";
      case MessageStatus.active:
        return "Aktivt";
      case MessageStatus.planned:
        return "Planerat";
      default:
        return "ERR";
    }
  }

  getStatusIcon(status: MessageStatus): string {
    switch (status) {
      case MessageStatus.closed:
        return "cancel";
      case MessageStatus.active:
        return "check_circle";
      case MessageStatus.planned:
        return "schedule";
      default:
        return "help";
    }
  }

  getPlatformIcon(messageType: MessageType): string {
    switch (messageType.toString()) {
      case "ti":
        return "phone_android";
      case "tot":
        return "train";
      default:
        return "help";
    }
  }

  hasTimeInterval(message: any): boolean {
    return message.timeInterval !== null && 
           (message.timeInterval.timeIntervalStart || message.timeInterval.timeIntervalEnd);
  }

  getTimeInterval(message: any): string {
    if (!this.hasTimeInterval(message)) {
      return '';
    }
    
    const formatTime = (time: string) => {
      if (!time) return '';
      const parts = time.split(':');
      return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : time;
    };
    
    const start = formatTime(message.timeInterval.timeIntervalStart) || '00:00';
    const end = formatTime(message.timeInterval.timeIntervalEnd) || '23:59';
    
    return `${start} - ${end}`;
  }


  onFilter(filter) {
    this.filter = filter;
    this.skip = 0;
    this.getMessagesWithFilter();
  }

  onLoadMore() {
    this.skip += this.take;
    this.getMessagesWithFilter(true);
  }

  onSort(event) {
    const { orderType, revertedOrder } = event;
    this.sortBy = orderType;
    this.sortDescending = revertedOrder;
    this.skip = 0;
    this.getMessagesWithFilter();
  }

  getMessagesWithFilter(append: boolean = false) {
    //Check that any status exists in filter
    if (
      this.filter.messageStatusesList &&
      this.filter.messageStatusesList.length > 0
    ) {
      if (append) {
        this.isLoadingMore = true;
      } else {
        this.messageService.loading = true;
      }
      this.errorText = null;

      const pagination = {
        skip: this.skip,
        take: this.take,
        sortBy: this.sortBy,
        sortDescending: this.sortDescending,
      };

      // Fetch messages with the current filter and pagination
      this.messageService.getAllMessagesAsync(this.filter, pagination).subscribe(
        (response: any) => {
          const items = Array.isArray(response) ? response : (response?.items ?? []);
          const mappedMessages = UtilityService.mapApiResponseArrayToMessageOverviews(items);

          this.total = response?.total ?? mappedMessages.length;
          this.hasMore = (this.skip + this.take) < this.total;

          if (mappedMessages.length == 0 && !append) {
            this.errorText = `Hittade inga meddelanden för valt filter`;
          } else {
            if (mappedMessages.length > 0) {
              this.messageService.latestUpdate = mappedMessages[0].lastUpdatedAt;
            }
            this.messageService.messageLength = this.total;
          }

          this.messageList = append
            ? [...this.messageList, ...mappedMessages]
            : mappedMessages;

          this.messageService.loading = false;
          this.isLoadingMore = false;

          if (!append && !mappedMessages.find(x => x.id == this.activeMessageId)) {
            this.onClickMessage(null, true);
          }
        },
        error => {
          console.error(JSON.stringify(error));
          this.messageList = append ? this.messageList : [];
          this.messageService.loading = false;
          this.isLoadingMore = false;
          this.errorText = `Misslyckades med att hämta meddelandelista. Försök igen.`;
        }
      );
    } else {
      this.messageList = [];
      this.total = 0;
      this.hasMore = false;
      this.errorText = `Ingen status vald i filtret`;
    }
  }

  onClickMessage(id: string | null, ignoreCheck?: boolean) {
    if(id === null){
      return;
    }

    if (this.activeMessageId != id) {
      if (ignoreCheck || !this.messageService.hasChanges()) {
        this.activeMessageId = id;
        this.messageService.currentMessage = null;
        this.messageService.originalMessage = null;
        this.clickMessage.emit(id);
      } else {
        this.dialogService.unsavedNavigationConfirmation().subscribe(result => {
          if (result) {
            this.activeMessageId = id;
            this.messageService.currentMessage = null;
            this.messageService.originalMessage = null;
            this.clickMessage.emit(id);
          }
        });
      }
    }
  }

  onClickNewMessage() {
    if (this.activeMessageId != null) {
      this.activeMessageId = null;
    }
  }

  validDate(dateToCheck: Date) {
    return UtilityService.validDate(dateToCheck);
  }

  isLoading() {
    return this.messageService.loading;
  }

}